import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    Paper,
    RadioGroup,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import { useEffect, useState } from "react";
import { useConfiguracoesPedidoContext } from "../../../pedidos/contexts/ConfiguracoesPedidoContext";
import { RadioCard } from "../../../core/components/RadioCard";
import { TipoCobrancaEntrega } from "../../../pedidos/enum/TipoCobrancaEntrega";
import {
    TipoTempoEntrega,
    TipoTempoEntregaDescricao,
    TipoTempoEntregaTitulo
} from "../../../pedidos/enum/TipoTempoEntrega";
import { IConfiguracaoTempoEntrega } from "../../../pedidos/interface/IConfiguracaoTempoEntrega";
import { useFetchMarketplaces } from "../../../marketplace/hooks/useFetchMarketplaces";
import { TipoMarketplace } from "../../../marketplace/enum/TipoMarketplace";

export const TempoEntrega = () => {
    const { configuracao, salvarTempoEntrega, resetarTempoEntrega } = useConfiguracoesPedidoContext();
    const { marketplaces } = useFetchMarketplaces();

    const [loading, setLoading] = useState(false);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

    const [tipo, setTipo] = useState<TipoTempoEntrega>(TipoTempoEntrega.FIXO);
    const [minEntregaFixo, setMinEntregaFixo] = useState<string>("");
    const [maxEntregaFixo, setMaxEntregaFixo] = useState<string>("");
    const [minRetiradaFixo, setMinRetiradaFixo] = useState<string>("");
    const [maxRetiradaFixo, setMaxRetiradaFixo] = useState<string>("");
    const [minPreparo, setMinPreparo] = useState<string>("");
    const [maxPreparo, setMaxPreparo] = useState<string>("");
    const [percentualAtraso, setPercentualAtraso] = useState<string>("");

    useEffect(() => {
        const config = configuracao?.tempoEntrega;
        if (config) {
            setTipo(config.tipo);
            setMinEntregaFixo(config.minEntregaFixo?.toString() ?? "");
            setMaxEntregaFixo(config.maxEntregaFixo?.toString() ?? "");
            setMinRetiradaFixo(config.minRetiradaFixo?.toString() ?? "");
            setMaxRetiradaFixo(config.maxRetiradaFixo?.toString() ?? "");
            setMinPreparo(config.minPreparo?.toString() ?? "");
            setMaxPreparo(config.maxPreparo?.toString() ?? "");
            setPercentualAtraso(config.percentualAtraso?.toString() ?? "");
        }
    }, [configuracao]);

    const marketplacesCardapioDigital = marketplaces?.filter(
        m => m.tipo === TipoMarketplace.CARDAPIO_DIGITAL_DELIVERY_RETIRADA
    );

    const todosUsandoDistancia =
        configuracao?.tipoCobrancaEntrega === TipoCobrancaEntrega.DISTANCIA &&
        (marketplacesCardapioDigital?.length === 0 ||
            marketplacesCardapioDigital?.every(
                m => m.configuracao?.tipoCobrancaEntrega === TipoCobrancaEntrega.DISTANCIA
            ));

    const handleSalvar = async () => {
        const config: IConfiguracaoTempoEntrega = { tipo };

        if (tipo === TipoTempoEntrega.FIXO) {
            config.minEntregaFixo = Number(minEntregaFixo);
            config.maxEntregaFixo = Number(maxEntregaFixo);
            config.minRetiradaFixo = Number(minRetiradaFixo);
            config.maxRetiradaFixo = Number(maxRetiradaFixo);
        } else {
            config.minPreparo = Number(minPreparo);
            config.maxPreparo = Number(maxPreparo);
            config.percentualAtraso = Number(percentualAtraso);
        }

        setLoading(true);
        await salvarTempoEntrega(config);
        setLoading(false);
    };

    const handleResetar = async () => {
        setConfirmDialogOpen(false);
        setLoading(true);
        await resetarTempoEntrega();
        setLoading(false);
    };

    return (
        <Box sx={{ width: "100%", display: "flex", justifyContent: "center", p: "1rem" }}>
            <Box sx={{ width: "100%", maxWidth: "700px" }}>
                <Typography variant="h5" fontWeight="bold" sx={{ mb: "1rem" }}>
                    Tempo de entrega
                </Typography>
                <Typography variant="subtitle2" color="GrayText" sx={{ mb: "1.5rem" }}>
                    Configure a previsão de tempo exibida ao cliente ao realizar um pedido de entrega ou retirada.
                    Esta configuração é opcional.
                </Typography>

                <Paper sx={{ p: "1.5rem", mb: "1rem" }}>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: "1rem" }}>
                        Modo de cálculo
                    </Typography>

                    <RadioGroup
                        value={tipo}
                        onChange={e => setTipo(e.target.value as TipoTempoEntrega)}
                        sx={{ gap: "0.5rem" }}
                    >
                        <RadioCard
                            labelValue={TipoTempoEntrega.FIXO}
                            labelTitle={TipoTempoEntregaTitulo.FIXO}
                            labelDescription={TipoTempoEntregaDescricao.FIXO}
                            selected={tipo === TipoTempoEntrega.FIXO}
                            onClick={() => setTipo(TipoTempoEntrega.FIXO)}
                        />

                        <Tooltip
                            title={
                                !todosUsandoDistancia
                                    ? "Disponível apenas quando PDV e cardápio digital usam cobrança por distância"
                                    : ""
                            }
                        >
                            <span>
                                <RadioCard
                                    labelValue={TipoTempoEntrega.PREPARO_MAIS_ROTA}
                                    labelTitle={TipoTempoEntregaTitulo.PREPARO_MAIS_ROTA}
                                    labelDescription={TipoTempoEntregaDescricao.PREPARO_MAIS_ROTA}
                                    selected={tipo === TipoTempoEntrega.PREPARO_MAIS_ROTA}
                                    onClick={() => todosUsandoDistancia && setTipo(TipoTempoEntrega.PREPARO_MAIS_ROTA)}
                                    fullWidth
                                />
                            </span>
                        </Tooltip>
                    </RadioGroup>
                </Paper>

                <Paper sx={{ p: "1.5rem", mb: "1rem" }}>
                    {tipo === TipoTempoEntrega.FIXO && (
                        <Box>
                            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: "1rem" }}>
                                Tempos fixos (em minutos)
                            </Typography>

                            <Typography variant="subtitle2" sx={{ mb: "1rem" }}>
                                Entrega
                            </Typography>
                            <Grid container spacing={2} sx={{ mb: "1rem" }}>
                                <Grid item xs={6}>
                                    <TextField
                                        label="Mínimo"
                                        type="number"
                                        fullWidth
                                        value={minEntregaFixo}
                                        onChange={e => setMinEntregaFixo(e.target.value)}
                                        inputProps={{ min: 0 }}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        label="Máximo"
                                        type="number"
                                        fullWidth
                                        value={maxEntregaFixo}
                                        onChange={e => setMaxEntregaFixo(e.target.value)}
                                        inputProps={{ min: 0 }}
                                    />
                                </Grid>
                            </Grid>

                            <Divider sx={{ mb: "1rem" }} />

                            <Typography variant="subtitle2" sx={{ mb: "1rem" }}>
                                Retirada
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <TextField
                                        label="Mínimo"
                                        type="number"
                                        fullWidth
                                        value={minRetiradaFixo}
                                        onChange={e => setMinRetiradaFixo(e.target.value)}
                                        inputProps={{ min: 0 }}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        label="Máximo"
                                        type="number"
                                        fullWidth
                                        value={maxRetiradaFixo}
                                        onChange={e => setMaxRetiradaFixo(e.target.value)}
                                        inputProps={{ min: 0 }}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    )}

                    {tipo === TipoTempoEntrega.PREPARO_MAIS_ROTA && (
                        <Box>
                            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: "0.5rem" }}>
                                Preparo + tempo de rota
                            </Typography>
                            <Alert severity="info" sx={{ mb: "1rem" }}>
                                O tempo de rota é calculado pelo Google Maps. O percentual de atraso é aplicado sobre
                                o tempo de rota e somado ao tempo de preparo.
                                Ex: preparo mín. 15min, rota 15min, 50% → mínimo final = 15 + 15 + 7,5 = 37,5 min
                            </Alert>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <TextField
                                        label="Preparo mínimo (min)"
                                        type="number"
                                        fullWidth
                                        value={minPreparo}
                                        onChange={e => setMinPreparo(e.target.value)}
                                        inputProps={{ min: 0 }}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        label="Preparo máximo (min)"
                                        type="number"
                                        fullWidth
                                        value={maxPreparo}
                                        onChange={e => setMaxPreparo(e.target.value)}
                                        inputProps={{ min: 0 }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        label="Percentual de atraso (%)"
                                        type="number"
                                        fullWidth
                                        value={percentualAtraso}
                                        onChange={e => setPercentualAtraso(e.target.value)}
                                        inputProps={{ min: 0 }}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    )}
                </Paper>

                <Box sx={{ display: "flex", gap: "1rem", justifyContent: "space-between" }}>
                    {configuracao?.tempoEntrega && (
                        <Button
                            variant="outlined"
                            color="error"
                            onClick={() => setConfirmDialogOpen(true)}
                            disabled={loading}
                        >
                            Remover configuração
                        </Button>
                    )}
                    <Button
                        variant="contained"
                        onClick={handleSalvar}
                        disabled={loading}
                        sx={{ ml: "auto" }}
                    >
                        {loading ? <CircularProgress size={24} /> : "Salvar"}
                    </Button>
                </Box>
            </Box>

            <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
                <DialogTitle>Remover configuração de tempo</DialogTitle>
                <DialogContent>
                    <Typography>
                        Tem certeza que deseja remover a configuração de tempo de entrega?
                        Os pedidos futuros não terão previsão de tempo exibida.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDialogOpen(false)}>Cancelar</Button>
                    <Button color="error" onClick={handleResetar}>
                        Remover
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
